import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import Select from 'react-select'
import { DatePicker } from 'antd'
import { Link } from 'react-router-dom'

import { stringifyDate, usePet, useToggle } from '../../modules'

import Receive from './receive'
import Pagination from './pagination'

const { RangePicker } = DatePicker

const recordTypes = [
    { label: 'Consultation', value: 'ConsultationRecord' },
    { label: 'Nursing', value: 'NursingRecord' },
    { label: 'Lab', value: 'LabRecord' },
    { label: 'Imaging', value: 'ImagingRecord' },
    { label: 'Physiotherapy', value: 'PhysiotherapyRecord' },
    { label: 'Theatre', value: 'TheatreRecord' },
    { label: 'Admission', value: 'AdmissionRecord' },
]

const colours = {
    ConsultationRecord: 'bg-blue-500 text-blue-700 bg-blue-50',
    NursingRecord: 'bg-green-500 text-green-700 bg-green-50',
    LabRecord: 'bg-purple-500 text-purple-700 bg-purple-50',
    ImagingRecord: 'bg-orange-500 text-orange-700 bg-orange-50',
    PhysiotherapyRecord: 'bg-cyan-500 text-cyan-700 bg-cyan-50',
    TheatreRecord: 'bg-rose-500 text-rose-700 bg-rose-50',
    AdmissionRecord: 'bg-yellow-500 text-yellow-700 bg-yellow-50',
}

const labels = recordTypes.reduce((acc, item) => ({ ...acc, [item.value]: item.label }), {})

const admissionOption = admission => {
    const activeStay = [...(admission?.wardHistory ?? [])].reverse().find(stay => stay?.status === 'occupied')
    const recentStay = activeStay ?? [...(admission?.wardHistory ?? [])].reverse()[0]
    const ward = recentStay?.ward?.name ?? recentStay?.ward
    const status = admission?.status === 'Admitted' ? 'Active' : admission?.status

    return {
        label: `Admitted ${stringifyDate(admission?.admittedOn)}${ward ? ` - ${ward}` : ''}${status ? ` (${status})` : ''}`,
        value: admission?._id,
    }
}

const Timeline = ({ patient, attachedTo, entryPathBase }) => {
    const patientId = patient?._id ?? patient
    const [entries, setEntries] = useState([])
    const [count, setCount] = useState(0)
    const [page, setPage] = useState(1)
    const [ranges, setRanges] = useState()
    const [selectedTypes, setSelectedTypes] = useState([])
    const [admissions, setAdmissions] = useState([{ label: 'Full Lifetime History', value: 'All' }])
    const [selectedAdmission, setSelectedAdmission] = useState({ label: 'Full Lifetime History', value: 'All' })
    const [includeOthers, toggleOthers] = useToggle()
    const limit = 20

    const effectiveAttachedTo = attachedTo ? (includeOthers ? undefined : attachedTo) : selectedAdmission?.value !== 'All' ? selectedAdmission?.value : undefined

    const timelinePayload = useMemo(() => ({
        patient: patientId,
        attachedTo: effectiveAttachedTo,
        recordTypes: selectedTypes.map(item => item.value),
        order: -1,
        page,
        limit,
        from: ranges?.[0]?._d ?? null,
        to: ranges?.[1]?._d ?? null,
    }), [effectiveAttachedTo, limit, page, patientId, ranges, selectedTypes])

    const [{ loading, error }, fetchTimeline] = usePet('patientTimeline', timelinePayload, {
        manual: true,
        notify: true,
        callback: ({ data }) => {
            setEntries(data.entries ?? [])
            setCount(data.count ?? 0)
        },
    })

    const [{ loading: aLoading, error: aError }, fetchAdmissions] = usePet('admissions', {
        patient: patientId,
        type: 'All',
        order: -1,
        limit: 100,
    }, {
        manual: true,
        notify: true,
        callback: ({ data }) => {
            setAdmissions([{ label: 'Full Lifetime History', value: 'All' }, ...(data.admissions ?? []).map(admissionOption)])
        },
    })

    useEffect(() => {
        if (patientId)
            fetchTimeline()
    }, [patientId, page, effectiveAttachedTo, ranges, selectedTypes]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!attachedTo && patientId)
            fetchAdmissions()
    }, [attachedTo, patientId]) // eslint-disable-line react-hooks/exhaustive-deps

    const moveTo = next => setPage(next)

    const resetFilters = () => setPage(1)

    const selectRange = range => {
        setRanges(range)
        resetFilters()
    }

    const selectTypes = types => {
        setSelectedTypes(types ?? [])
        resetFilters()
    }

    const selectAdmission = admission => {
        setSelectedAdmission(admission)
        resetFilters()
    }

    const toggleScope = () => {
        toggleOthers()
        resetFilters()
    }

    const linkFor = entry => {
        if (entry?.sourceType === 'Admission')
            return `/app/inpatient/admissions/${entry?.ref?._id ?? entry?.ref}`

        const ref = entry?.ref?._id ?? entry?.ref
        return ref && entryPathBase ? `${entryPathBase}/${ref}` : null
    }

    return (
        <>
            <div className="grid gap-3 md:grid-cols-3 sm:grid-cols-1 mb-3">
                <div>
                    <p className="text-gray-500 text-xs font-semibold mb-2">Record Type</p>
                    <Select
                        isMulti
                        value={selectedTypes}
                        onChange={selectTypes}
                        options={recordTypes}
                        placeholder="All record types" />
                </div>
                <div>
                    <p className="text-gray-500 text-xs font-semibold mb-2">Date Range</p>
                    <RangePicker className="w-full" onChange={selectRange} value={ranges} />
                </div>
                {
                    !attachedTo &&
                    <div>
                        <p className="text-gray-500 text-xs font-semibold mb-2">Admission Scope</p>
                        <Receive progress loading={aLoading} error={aError}>
                            <Select
                                value={selectedAdmission}
                                onChange={selectAdmission}
                                options={admissions}
                                placeholder="Full Lifetime History" />
                        </Receive>
                    </div>
                }
            </div>
            {
                attachedTo &&
                <span className="mb-3 inline-flex items-center">
                    <input
                        onChange={toggleScope}
                        checked={includeOthers}
                        type="checkbox"
                        className="form-checkbox cursor-pointer h-5 w-5" />
                    <span onClick={toggleScope} className="text-gray-500 cursor-pointer text-xs italic ml-2">Show full patient history</span>
                </span>
            }
            <Receive progress loading={loading} error={error}>
                {
                    entries.length > 0 ?
                        <>
                            <div className="space-y-3">
                                {
                                    entries.map(entry => {
                                        const badge = colours[entry?.recordType] ?? 'bg-gray-500 text-gray-700 bg-gray-50'
                                        const link = linkFor(entry)

                                        return (
                                            <div key={entry._id} className="flex flex-col justify-between shadow-lg rounded-sm border border-gray-200 px-4 pt-3 pb-3">
                                                <div className="flex items-start justify-between gap-4">
                                                    <div className="flex items-start">
                                                        <div className={`${badge.split(' ')[0]} rounded-full h-4 w-4 border-gray-200 border-2 mt-1`}></div>
                                                        <div className="ml-4">
                                                            <div className="flex flex-wrap items-center gap-2">
                                                                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${badge.split(' ').slice(1).join(' ')}`}>
                                                                    {labels[entry?.recordType] ?? entry?.recordType ?? 'Record'}
                                                                </span>
                                                                {
                                                                    (entry?.specialty || entry?.department) &&
                                                                    <span className="text-xs text-gray-500">
                                                                        {[entry?.specialty, entry?.department].filter(Boolean).join(' · ')}
                                                                    </span>
                                                                }
                                                            </div>
                                                            <p className="font-medium text-sm mt-2 mb-1">{entry?.summary}</p>
                                                            <p className="text-gray-500 my-0 text-xs italic">
                                                                {stringifyDate(entry?.date)} {entry?.filledBy?.name ? `· By ${entry?.filledBy?.name}` : ''}
                                                            </p>
                                                            {
                                                                entry?.item?.name &&
                                                                <p className="text-gray-500 my-0 text-xs italic">{entry.item.name}</p>
                                                            }
                                                        </div>
                                                    </div>
                                                    {
                                                        link &&
                                                        <Link to={link}>
                                                            <button className="btn py-2 hisani-btn-green-light text-white whitespace-nowrap">View</button>
                                                        </Link>
                                                    }
                                                </div>
                                            </div>
                                        )
                                    })
                                }
                            </div>
                            <Pagination total={count} limit={limit} page={page} navigate={moveTo} />
                        </>
                        :
                        <div className="w-full h-24 flex items-center justify-center">
                            <p className="font-semibold text-base">
                                No timeline entries were found{ranges?.[0] || ranges?.[1] ? ` for ${moment(ranges?.[0]?._d ?? new Date()).format('DD MMM YYYY')} - ${moment(ranges?.[1]?._d ?? new Date()).format('DD MMM YYYY')}` : ''}
                            </p>
                        </div>
                }
            </Receive>
        </>
    )
}

export default Timeline
